import { isImmediateNuPayment, isTrackedCard } from "./finance";
import type { FinancialSnapshot, Movement } from "./types";

export interface MovementEditValues {
  billedAmountCents: number;
  reimbursementCents: number;
  fixedExpense: boolean;
}

export type MovementEditPlan =
  | {
    allowed: true;
    oldRealCents: number;
    nextRealCents: number;
    oldOwnWeeklyCents: number;
    nextOwnWeeklyCents: number;
    persistentSavingsCents: number;
    oldWeeklyImpactCents: number;
    nextWeeklyImpactCents: number;
    weekDeltaCents: number;
    savingsRescueCents: number;
    weekAvailableAfterCents: number;
    freeSavingsAfterCents: number;
  }
  | { allowed: false; reason: "invalid_amount" | "week_not_found" | "insufficient_savings"; missingCents?: number };

function ownWeeklyCents(movement: Movement, billedAmountCents: number, reimbursementCents: number, fixedExpense: boolean): number {
  if (fixedExpense) return 0;
  const own = Math.max(0, billedAmountCents - reimbursementCents);
  if (!isTrackedCard(movement.paymentMethod)) return own;
  if (movement.cardFundingMode === "later") return 0;
  if (movement.cardFundingMode === "partial") return Math.min(own, Math.max(0, movement.amountTodayCents ?? 0));
  return own;
}

/**
 * Planea una corrección sin recalcular el ahorro que ya fue apartado.
 * Si la semana no alcanza para un aumento, propone rescatar sólo la diferencia
 * del ahorro libre; la capa de Firebase exige confirmación explícita.
 */
export function planMovementEdit(snapshot: FinancialSnapshot, movement: Movement, values: MovementEditValues): MovementEditPlan {
  const billed = values.billedAmountCents;
  const reimbursement = values.reimbursementCents;
  if (!Number.isSafeInteger(billed) || billed <= 0 || !Number.isSafeInteger(reimbursement) || reimbursement < 0 || reimbursement > billed) {
    return { allowed: false, reason: "invalid_amount" };
  }
  const week = snapshot.weeks.find((candidate) => candidate.id === movement.weekId);
  if (!week) return { allowed: false, reason: "week_not_found" };

  const oldBilled = Math.max(0, movement.billedAmountCents ?? movement.amountCents);
  const oldReimbursement = Math.max(0, movement.reimbursementCents ?? 0);
  const oldFixed = Boolean(movement.fixedExpense || movement.budgetImpact === false);
  const persistentSavingsCents = Math.max(0, movement.savingsContributionCents ?? 0);
  const oldOwnWeeklyCents = ownWeeklyCents(movement, oldBilled, oldReimbursement, oldFixed);
  const nextOwnWeeklyCents = ownWeeklyCents(movement, billed, reimbursement, values.fixedExpense);
  const oldWeeklyImpactCents = Math.max(0, movement.weeklyImpactCents ?? oldOwnWeeklyCents + persistentSavingsCents);
  // El ahorro ya hecho sigue siendo uso histórico de la semana aunque el gasto
  // se marque después como fijo o cambie de importe.
  const nextWeeklyImpactCents = nextOwnWeeklyCents + persistentSavingsCents;
  const weekDeltaCents = nextWeeklyImpactCents - oldWeeklyImpactCents;
  const savingsRescueCents = Math.max(0, weekDeltaCents - week.availableCents);
  const allocatedSavingsCents = Math.max(snapshot.protectedSavingsCents, snapshot.allocatedSavingsCents ?? snapshot.protectedSavingsCents);
  const freeSavingsCents = Math.max(0, snapshot.savingsCents - allocatedSavingsCents);
  if (savingsRescueCents > freeSavingsCents) {
    return { allowed: false, reason: "insufficient_savings", missingCents: savingsRescueCents - freeSavingsCents };
  }
  return {
    allowed: true,
    oldRealCents: Math.max(0, oldBilled - oldReimbursement),
    nextRealCents: billed - reimbursement,
    oldOwnWeeklyCents,
    nextOwnWeeklyCents,
    persistentSavingsCents,
    oldWeeklyImpactCents,
    nextWeeklyImpactCents,
    weekDeltaCents,
    savingsRescueCents,
    weekAvailableAfterCents: week.availableCents - weekDeltaCents + savingsRescueCents,
    freeSavingsAfterCents: freeSavingsCents - savingsRescueCents,
  };
}

/** Conserva el impacto histórico y aplica sólo la diferencia de la edición. */
export function editedAccountDeltaCents(movement: Movement, previousAccountDeltaCents: number, billedCents: number, reimbursementCents: number): number {
  return previousAccountDeltaCents
    + (movement.reimbursementReceived ? reimbursementCents - (movement.reimbursementCents ?? 0) : 0)
    - (isImmediateNuPayment(movement.paymentMethod) ? billedCents - (movement.billedAmountCents ?? movement.amountCents) : 0);
}
